import { Link } from 'react-router-dom';
import { TeacherShell } from './Layout.tsx';

/**
 * Public marketing page for the school product.
 *
 * Phase 2 / ThinkAhead Lab in-school enrichment programs. Doesn't require
 * authentication — teachers land here from a campaign / docs link, hit
 * "Sign in" to enter the dashboard.
 */
export function TeacherLanding(): JSX.Element {
  return (
    <TeacherShell
      trailingNav={
        <>
          <Link
            to="/join"
            className="rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:text-amber-700"
          >
            Student? Join a class
          </Link>
          <Link
            to="/teacher/sign-in"
            className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-600"
          >
            Teacher sign-in
          </Link>
        </>
      }
    >
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
        <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">
          ThinkAhead Lab in-school
        </p>
        <h1 className="mt-3 text-4xl font-extrabold text-slate-900 sm:text-5xl">
          3D modeling for the classroom — without the CAD tax.
        </h1>
        <p className="mt-6 text-lg text-slate-700">
          Maysterko is a kid-first 3D modeling app built for ages 8–12. The school edition adds
          teacher-authored lessons, a join-by-code roster, and a live-session view that lets you see
          every student's design at a glance — on any browser, no install required.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/teacher/sign-in"
            className="rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600"
          >
            Get started — teacher sign-in
          </Link>
          <Link
            to="/join"
            className="rounded-2xl border-2 border-slate-200 bg-white px-6 py-4 text-base font-semibold text-slate-700 shadow-sm transition active:scale-95 hover:border-amber-300"
          >
            Student? Enter a join code
          </Link>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-3">
          <Feature
            title="Authoring"
            body="Build short lessons with a chain of challenges. Reuse the same shape primitives kids see on the canvas."
          />
          <Feature
            title="Live session"
            body="Roster + join code on the room TV. Spot stuck students at a glance and unblock them in seconds."
          />
          <Feature
            title="Review"
            body="Every student's design captured as a thumbnail, scrollable across the period and across sessions."
          />
        </div>
      </section>
    </TeacherShell>
  );
}

function Feature({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div className="rounded-2xl border border-amber-100 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      <p className="mt-2 text-sm text-slate-600">{body}</p>
    </div>
  );
}
